<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>Issue Boxes Per Location</h5>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label-for="grn"
              label="SIN #"
            >
              <validation-provider
                #default="{ errors }"
                name="grn"
                rules=""
              >
                <b-form-input
                  id="grn"
                  v-model="ledger.grn"
                  name="grn"
                  :state="errors.length > 0 ? false : null"
                  size="sm"
                />
                <small class="text-danger">{{ errors[0] }}</small>
                <small
                  v-if="serverErrors && serverErrors.grn"
                  class="text-danger"
                >{{ serverErrors.grn[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="9"
          >
            <b-row>
              <!-- Type -->
              <b-col
                cols="12"
                md="3"
                class="mb-md-0 mb-2"
              >
                <b-form-group
                  label="Type"
                  label-for="type"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="type"
                    rules=""
                  >
                    <b-form-select
                      id="type"
                      v-model="ledger.type"
                      name="type"
                      size="sm"
                      @change="changeTicketType"
                    >
                      <b-form-select-option
                        v-for="type in ticketTypes"
                        :key="type.id"
                        :value="type.id"
                        :selected="type.id === null"
                      >
                        {{ type.text }}
                      </b-form-select-option>
                    </b-form-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="serverErrors && serverErrors.type"
                      class="text-danger"
                    >{{ serverErrors.type[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Location -->
              <b-col
                cols="12"
                md="9"
                class="mb-md-0 mb-2"
              >
                <b-row>
                  <b-col cols="12">
                    <location-filters
                      size="sm"
                      align="horizontal"
                      @filter="changeFilter"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row>
          <!-- Boxes - Filtered by Type -->
          <b-col
            cols="12"
            md="12"
            class="mb-md-0 mb-2"
          >
            <b-form-group
              label="Boxes"
              label-for="boxes"
            >
              <v-select
                id="boxes"
                v-model="ledger.boxes"
                :options="boxOptions"
                label="number"
                :reduce="o => o.id"
                multiple
                :clearable="true"
                :disabled="!ledger.type"
                placeholder="First select a Type"
              >
                <template #no-options>
                  <span
                    v-if="!ledger.type"
                    class="text-muted"
                  >Please select Type first</span>
                  <span
                    v-else
                    class="text-muted"
                  >No boxes available for this type</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            md="12"
            class="text-right"
          >
            <b-button
              size="sm"
              variant="primary"
              :disabled="saving"
              @click="submit"
            >
              <b-spinner
                v-if="saving"
                small
                class="mr-1"
              />
              <span v-if="!saving">Save</span>
              <span v-else>Saving...</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- <b-row class="mt-3">
          <pre style="width:100%;white-space:pre-wrap">{{ ledger }}</pre>
        </b-row> -->
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label>entries</label>
          </b-col>
          <!-- ./Per Page -->

          <!-- Search & Button -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
                size="sm"
              />
              <b-button
                variant="primary"
                size="sm"
                @click="invokeCreateForm()"
              >
                <span class="text-nowrap">Register Grade</span>
              </b-button>
            </div>
          </b-col>
          <!-- ./Search & Button-->
        </b-row>
        <!-- ./Table Top -->
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        :busy="isBusy"
        striped
        hover
        small
        class="position-relative"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- <template #cell(isActive)="data">
              <b-badge pill :variant="`light-${resolveStatusVariant(data.item.isActive)}`" class="text-capitalize">
                  <span v-if="!data.item.isActive">Blocked</span>
                  <span v-else>Active</span>
              </b-badge>
          </template> -->

        <template #cell(type)="data">
          {{ data.item.items[0].item.type }}
        </template>

        <template #cell(quantity)="data">
          {{ parseFloat(data.item.quantity).toLocaleString() }}
        </template>

        <template #cell(actual)="data">
          {{ parseFloat(data.item.actual).toLocaleString() }}
        </template>

        <template #cell(received)="data">
          <span v-if="data.item.received">Received</span>
          <span v-else>Not Received</span>
        </template>

        <template #cell(items)="data">
          {{ data.item.items.length.toLocaleString() }}
        </template>

        <template #cell(offloaded)="data">
          {{ data.item.items.filter((item) => item.received).length.toLocaleString() }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="invokeUpdateForm(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="remove(data.item.id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Table Footer -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BRow, BCol, BCard, BButton, BCardHeader, BCardBody, BTable, BPagination,
    BFormSelect, BFormSelectOption, BFormInput, BDropdown, BDropdownItem,
    BFormGroup, BBadge, BSpinner,
} from 'bootstrap-vue'
import LocationFilters from '@core/components/cromis/filters/LocationFilters.vue'
import vSelect from 'vue-select'
import issueBoxesStoreModule from '@/views/cromis/inventory/issuing/issueBoxesStoreModule'
import useIssueBoxesList from '@/views/cromis/inventory/issuing/useIssueBoxesList.js'
import store from '@/store'
import {
    ref, onUnmounted, onMounted, reactive,
} from '@vue/composition-api'

export default {
    name: 'IssueBoxFilter',
    components: {
        BRow,
        BCol,
        BCard,
        BButton,
        BCardHeader,
        BCardBody,
        BTable,
        BPagination,
        BFormGroup,
        BFormSelect,
        BFormSelectOption,
        BBadge,
        vSelect,
        BFormInput,
        BDropdown,
        BDropdownItem,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
        LocationFilters,
    },
    directives: {},
    props: {
        storageKey: { type: String, default: 'userData' },
    },

    setup(props, context) {
        const saving = ref(false)
        const serverErrors = ref(null)
        const baleTickets = ref([])
        const baleTags = ref([])
        const sweepingTickets = ref([])
        const boxes = ref([])
        const boxOptions = ref([])
        const selectedBoxIds = ref([])
        // const normalizedBoxIds = ref([])
        const ticketTypes = ref([
            { id: null, text: 'Select...' },
            { id: 1, text: 'Tickets Boxes' },
            { id: 2, text: 'Tags Boxes' },
            { id: 3, text: 'Sweeping Tickets' },
        ])

        const ledger = ref({
            id: null,
            grn: null,
            type: null,
            location_id: null,
            boxes: [],
            action: 'issue',
        })

        const CROMIS_STORE_MODULE_NAME = 'cromis-issue-box'

        // Register module
        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) store.registerModule(CROMIS_STORE_MODULE_NAME, issueBoxesStoreModule)

        // UnRegister on leave
        onUnmounted(() => {
            if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME)
        })

        onMounted(async () => {
            await store.dispatch('cromis-issue-box/boxes')
                .then(response => {
                    const { boxes } = response.data

                    baleTickets.value = boxes.filter(box => parseInt(`${box.type}`) === 1)
                    baleTags.value = boxes.filter(box => parseInt(`${box.type}`) === 2)
                    sweepingTickets.value = boxes.filter(box => parseInt(`${box.type}`) === 3)
                })
                .catch(error => {
                    console.log('got error', error)
                })
        })

        const {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,
            isBusy,

            // Filers
            resolveStatusVariant,
            resolveReceivedVariant,
            locationFilter,
        } = useIssueBoxesList()

        const submit = async evt => {
            if (evt && evt.preventDefault) evt.preventDefault()

            if (!ledger.value.grn || !ledger.value.boxes.length || !ledger.value.location_id || !ledger.value.type) {
                this.$swal({ icon: 'warning', text: 'Please provide SIN number, select box type, location, and at least one box!' })
                return
            }

            saving.value = true

            await store.dispatch('cromis-issue-box/create', ledger.value)
                .then(response => {
                    saving.value = false
                    // this.$swal({ icon: 'success', text: 'Boxes issued successfully!' })

                    context.root.$swal({
                        icon: 'success',
                        text: `Boxes in SIN ${ledger.value.grn} issued successfully!`,
                        showConfirmButton: true,
                        timer: 3000,
                        customClass: {
                            confirmButton: 'btn btn-success',
                        },
                        buttonsStyling: false,
                    })

                    // Reset form
                    ledger.value = {
                        id: null,
                        grn: null,
                        type: null,
                        location_id: null,
                        boxes: [],
                    }

                    boxOptions.value = []
                })
                .catch(error => {
                    saving.value = false
                    if (error.response && error.response.status === 422) {
                        serverErrors.value = error.response.data.errors
                    } else {
                        // this.$swal({ icon: 'error', text: 'An error occurred while issuing boxes. Please try again later.' })

                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'An error occurred while issuing boxes. Please try again later. Consult tech support',
                            showConfirmButton: true,
                            customClass: {
                                confirmButton: 'btn btn-danger',
                            },
                            buttonsStyling: false,
                        })
                    }
                })
        }

        const changeFilter = filteredValue => {
            ledger.value.location_id = filteredValue != null ? filteredValue.id : null
        }

        const changeTicketType = () => {
            ledger.value.boxes = []
            boxOptions.value = ledger.value.type === null
                ? []
                : ledger.value.type === 1
                    ? baleTickets.value
                    : (ledger.value.type === 3 ? sweepingTickets.value : baleTags.value)
        }

        return {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,
            isBusy,
            resolveStatusVariant,
            resolveReceivedVariant,

            saving,
            serverErrors,

            boxOptions,
            selectedBoxIds,
            ticketTypes,
            boxes,
            ledger,

            changeFilter,
            changeTicketType,
            submit,
        }
    },
}
</script>

<style scoped lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
